import React, { useState } from 'react';
import { motion } from 'framer-motion';

const PortfolioPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'E-Commerce Platform',
      category: 'web',
      image: 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A modern e-commerce platform with advanced product filtering and secure payment processing.',
      client: 'RetailTech Inc.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe API']
    },
    {
      id: 2,
      title: 'Health & Fitness App',
      category: 'mobile',
      image: 'https://images.pexels.com/photos/4498362/pexels-photo-4498362.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A comprehensive health and fitness app with workout tracking and nutrition planning features.',
      client: 'FitLife Solutions',
      technologies: ['React Native', 'Firebase', 'HealthKit API']
    },
    {
      id: 3,
      title: 'Financial Management System',
      category: 'enterprise',
      image: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'An enterprise-grade financial management system with advanced reporting and analytics.',
      client: 'Global Finance Corp',
      technologies: ['Angular', 'Java Spring', 'PostgreSQL', 'Docker']
    },
    {
      id: 4,
      title: 'Real Estate Platform',
      category: 'web',
      image: 'https://images.pexels.com/photos/186077/pexels-photo-186077.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A property listing and management platform with interactive maps and virtual tours.',
      client: 'HomeQuest Realty',
      technologies: ['React', 'Node.js', 'MongoDB', 'Google Maps API']
    },
    {
      id: 5,
      title: 'Food Delivery App',
      category: 'mobile',
      image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A user-friendly food delivery app with real-time order tracking and restaurant management.',
      client: 'QuickBite Services',
      technologies: ['Flutter', 'Firebase', 'Google Maps API']
    },
    {
      id: 6,
      title: 'Inventory Management System',
      category: 'enterprise',
      image: 'https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A comprehensive inventory management system with barcode scanning and automated reordering.',
      client: 'SupplyChain Solutions',
      technologies: ['Vue.js', 'Python Django', 'PostgreSQL']
    },
    {
      id: 7,
      title: 'Educational Platform',
      category: 'web',
      image: 'https://images.pexels.com/photos/301926/pexels-photo-301926.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'An interactive learning platform with course management and student progress tracking.',
      client: 'EduTech Innovations',
      technologies: ['React', 'Node.js', 'MongoDB', 'WebRTC']
    },
    {
      id: 8,
      title: 'Healthcare Management System',
      category: 'enterprise',
      image: 'https://images.pexels.com/photos/7089401/pexels-photo-7089401.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A secure healthcare management system for patient records and appointment scheduling.',
      client: 'MediCare Systems',
      technologies: ['Angular', 'Java Spring', 'Oracle', 'HIPAA Compliant']
    },
    {
      id: 9,
      title: 'Travel Companion App',
      category: 'mobile',
      image: 'https://images.pexels.com/photos/1051075/pexels-photo-1051075.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A travel app with itinerary planning, booking integration, and offline maps.',
      client: 'Wanderlust Travels',
      technologies: ['React Native', 'GraphQL', 'MongoDB']
    }
  ];

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'web', label: 'Web Apps' },
    { id: 'mobile', label: 'Mobile Apps' },
    { id: 'enterprise', label: 'Enterprise Solutions' },
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-secondary-900 to-secondary-800 text-white">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="mb-6 text-4xl font-bold md:text-5xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Our Portfolio
            </motion.h1>
            <motion.p 
              className="text-lg text-secondary-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Explore our recent projects and discover how we've helped businesses achieve their goals through innovative software solutions.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="section">
        <div className="container">
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`px-5 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeFilter === filter.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-primary-50'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <motion.div 
            className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
            layout
          >
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                className="overflow-hidden bg-white rounded-lg shadow-lg"
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative overflow-hidden h-60">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="object-cover w-full h-full transition-transform duration-500 hover:scale-110"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 bg-primary-900 bg-opacity-70 hover:opacity-100 transition-opacity duration-300">
                    <button className="px-4 py-2 text-sm font-medium text-white border-2 border-white rounded-md hover:bg-white hover:text-primary-600 transition-colors">
                      View Details
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="mb-2 text-xl font-semibold">{project.title}</h3>
                  <p className="mb-4 text-secondary-600">{project.description}</p>
                  <div className="mb-4">
                    <p className="text-sm text-secondary-500">
                      <span className="font-medium">Client:</span> {project.client}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded-full"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="px-3 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded-full">
                      {filters.find(f => f.id === project.category)?.label.replace(' Apps', '').replace(' Solutions', '')}
                    </span>
                    <button className="text-sm font-medium text-primary-600 hover:text-primary-700">
                      Case Study
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6 text-3xl font-bold">Ready to Start Your Project?</h2>
            <p className="mb-8 text-lg">
              Contact us today to discuss how we can help bring your ideas to life.
            </p>
            <a 
              href="/contact" 
              className="px-8 py-3 text-primary-600 bg-white rounded-md hover:bg-secondary-100 transition-colors"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default PortfolioPage;
